/*
  "Complete the set" in the cart drawer (snippets/atlas-addons.liquid), and the test
  that decides whether it replaces Rebuy's popup.

  The test ended 2026-10-27 with b the winner: layout/theme.liquid now puts every visitor
  in arm b. What follows is how it ran.

  THE TEST. layout/theme.liquid puts every visitor in arm a or b on the first page, before
  anything paints, and keeps it 90 days in the atlas_ab_addons cookie (?addons=a or
  ?addons=b sets it by hand, for checking):

    a  Rebuy's "You'll also love" popup after add to cart, as before; this block hidden.
    b  no popup; this block in the drawer.

  Everything else Rebuy does (the bundle on the product page, the cart page widget) is the
  same in both arms, so the difference in orders is these two things alone. The arm goes
  on the cart as the hidden attribute _ab_addons, so every order says which arm it came
  from, and an add-on added here carries the hidden line property _atlas_addon (drawer or page).
  Revenue per visitor by arm is the verdict; the split is even, so orders and revenue per
  arm compare directly.

  THE ADD. A tap adds in place and Shopify re-renders the drawer (the way the product form
  does it) or the cart page's sections (the way a quantity change does it), so the new line
  appears above and the block answers the new cart. The line property says where: drawer or
  page.
  If the add is refused (stock, a minimum), the cart page says why.
*/
(() => {
  if (window.atlasAddons) return;
  window.atlasAddons = true;

  const arm = document.documentElement.getAttribute('data-ab-addons');
  const POPUP = '245272';

  // Arm b: take Rebuy's popup out of the page before Rebuy runs (it loads on the first
  // tap, see atlas-script-block). Without its container it has nothing to open.
  const dropPopup = () => {
    if (arm !== 'b') return;
    document.querySelectorAll(`[id$="__rebuy_${POPUP}"], #rebuy-widget-${POPUP}`).forEach((el) => el.remove());
  };
  dropPopup();
  document.addEventListener('DOMContentLoaded', dropPopup);

  // The third slot: one of its contenders per cart, the same one every time this cart shows
  // the same contenders (FNV-1a over the cart token and their ids).
  const hash = (text) => {
    let h = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };
  const pickContender = (block) => {
    const contenders = [...block.querySelectorAll('[data-atlas-contender]')];
    if (!contenders.length || contenders.some((c) => c.classList.contains('is-picked'))) return;
    const key = (block.dataset.cartToken || '') + contenders.map((c) => c.dataset.pid).join(',');
    contenders[hash(key) % contenders.length].classList.add('is-picked');
  };

  // Put the arm on the cart once it has items, and in arm b what the shopper was shown
  // (_atlas_seen, product ids, the latest 40), so the build learns from sightings and adds
  // alike. The blocks render the cart's current values, so this costs one request when
  // something new was shown, not one per page.
  let syncing = false;
  const syncArm = () => {
    const blocks = [...document.querySelectorAll('[data-atlas-addons]')];
    blocks.forEach(pickContender);
    if (!arm || syncing || !blocks.length) return;
    const attributes = {};
    if (blocks[0].dataset.abAttr !== arm) attributes._ab_addons = arm;
    if (arm === 'b') {
      const seen = (blocks[0].dataset.seenAttr || '').split(',').filter(Boolean);
      const before = seen.length;
      blocks.forEach((block) => {
        block.querySelectorAll('.atlas-addons__item').forEach((item) => {
          const shown = !item.hasAttribute('data-atlas-contender') || item.classList.contains('is-picked');
          if (shown && !seen.includes(item.dataset.pid)) seen.push(item.dataset.pid);
        });
      });
      if (seen.length > before) attributes._atlas_seen = seen.slice(-40).join(',');
    }
    if (!Object.keys(attributes).length) return;
    syncing = true;
    fetch(`${window.routes ? window.routes.cart_update_url : '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then(() =>
        document.querySelectorAll('[data-atlas-addons]').forEach((b) => {
          if (attributes._ab_addons) b.dataset.abAttr = attributes._ab_addons;
          if (attributes._atlas_seen) b.dataset.seenAttr = attributes._atlas_seen;
        })
      )
      .catch(() => {})
      .finally(() => (syncing = false));
  };

  const watch = () => {
    syncArm();
    ['cart-drawer', 'cart-items'].forEach((tag) => {
      const host = document.querySelector(tag);
      if (host) new MutationObserver(syncArm).observe(host, { childList: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();

  // The cart page: the same sections Dawn re-renders after a quantity change.
  const renderPage = (cartItems, json) => {
    cartItems.getSectionsToRender().forEach((section) => {
      const host = document.getElementById(section.id);
      const html = json.sections[section.section];
      if (!host || !html) return;
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-atlas-addon]');
    if (!button) return;
    const drawer = button.closest('cart-drawer');
    const page = drawer ? null : button.closest('cart-items');
    if (drawer && typeof drawer.renderContents !== 'function') return;
    if (!drawer && !(page && typeof page.getSectionsToRender === 'function')) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    // One variant id, or a comma list with a matching list of quantities (the free shipping
    // nudge can offer two products at once).
    const ids = String(button.dataset.atlasAddon).split(',').map(Number);
    const quantities = String(button.dataset.quantity || '').split(',').map(Number);
    const id = ids[0];
    const sections = drawer
      ? ['cart-drawer', 'cart-icon-bubble']
      : page.getSectionsToRender().map((section) => section.section);
    fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map((vid, i) => ({ id: vid, quantity: quantities[i] || 1, properties: { _atlas_addon: button.dataset.place || 'drawer' } })),
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || !json.sections) throw new Error(json.description || 'add failed');
        if (drawer) drawer.renderContents({ id, sections: json.sections });
        else renderPage(page, json);
        syncArm();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'atlas-addons', productVariantId: id, cartData: json });
        }
      })
      .catch(() => {
        // The cart page says what went wrong (a minimum, stock) better than a toast would.
        window.location.href = window.routes ? window.routes.cart_url : '/cart';
      });
  });
})();
